import React, { useState } from "react";
import { 
  Grid, 
  Avatar, 
  Typography, 
  Button, 
  Box, 
  CircularProgress,
  useTheme,
  useMediaQuery 
} from "@mui/material";

interface ImageUploaderProps {
  imageUrl: string;
  onImageChange: (url: string) => void;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ imageUrl, onImageChange }) => {
  const [uploading, setUploading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      setUploading(true);
      const response = await fetch("https://gg-api.nationtonation.ca/api/products/upload", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.imageUrl) {
        onImageChange(data.imageUrl);
      } else {
        console.error("❌ Upload failed:", data.error);
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box',
      p: 1
    }}>
      <Grid 
        container 
        spacing={2} 
        alignItems="center"
        sx={{
          width: '100%',
          margin: 0,
          '& .MuiGrid-item': {
            paddingTop: '8px !important',
            paddingBottom: '8px !important',
            paddingLeft: '8px !important',
            paddingRight: '8px !important',
            [theme.breakpoints.down('sm')]: {
              paddingLeft: '0 !important',
              paddingRight: '0 !important'
            }
          }
        }}
      >
        <Grid size={{xs: 12, sm: 6 }}>
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
            fullWidth
            sx={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {uploading ? (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            ) : "Upload Image"}
            <input 
              type="file" 
              hidden 
              accept="image/*" 
              onChange={handleUpload} 
            />
          </Button>
        </Grid>

        <Grid 
          size={{xs: 12, sm: 6}} 
      
          sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: isMobile ? 'auto' : '100%'
          }}
        >
          {imageUrl ? (
            <Box
              sx={{
                width: '100%',
                maxWidth: 120,
                height: 120,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <img
                src={imageUrl}
                alt="Uploaded preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </Box>
          ) : (
            <Typography 
              variant="body2"
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic'
              }}
            >
              No image uploaded
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImageUploader;